import React, { useEffect, useState, useRef } from "react";
import { BACKEND_URL } from "../constants";
import { useAuth } from "../navigation/AuthProvider";
import { FaTrash} from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
const TrashPopup = ({
  visible,
  onClose,
  companyId,
  timeSchemaId,
  userId,
  onRestoreShifts
}) => {
  const [deletedShifts, setDeletedShifts] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const abortRef = useRef(null);

  const { projects, employees, draftSchedule, setDraftSchedule,} = useAuth();

  useEffect(() => {
    if (!visible) return;
    fetchData();
    return () => abortRef.current?.abort();
  }, [visible]);

  const fetchData = async () => {
    abortRef.current = new AbortController();

    const res = await fetch(`${BACKEND_URL}/api/get-deleted-shifts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal: abortRef.current.signal,
      body: JSON.stringify({ userId, companyId, timeSchemaId }),
    });

    const data = await res.json();
    if (data.status === "success") {
      setDeletedShifts(data.shifts || []);
    }
  };

  // 🔥 GROUPING
  const group = (shifts) => {
    const sorted = [...shifts].sort(
      (a, b) => new Date(b.deletedAt) - new Date(a.deletedAt)
    );

    const res = [];
    let current = [];
    const LIMIT = 10 * 60 * 1000;

    for (let s of sorted) {
      const prev = current[current.length - 1];

      if (!prev) {
        current.push(s);
        continue;
      }

      const sameUser = String(prev.deletedBy) === String(s.deletedBy);
      const diff = new Date(prev.deletedAt) - new Date(s.deletedAt);

      if (sameUser && diff <= LIMIT) {
        current.push(s);
      } else {
        res.push(current);
        current = [s];
      }
    }

    if (current.length) res.push(current);

    return res;
  };

  const groups = group(deletedShifts);

  const restore = async (group) => {
    if (restoring) return;

    setRestoring(true);

    const ids = group.map((s) => s._id);

    const res = await fetch(`${BACKEND_URL}/api/restore-shift`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, companyId, shiftIds: ids }),
    });

    const data = await res.json();

    if (data.status === "success") {
        // 🔥 remove from trash UI
        setDeletedShifts(prev =>
        prev.filter(s => !ids.includes(s._id))
        );

        // 🔥 UPDATE MAIN UI
        onRestoreShifts?.(data.restoredShifts);
    }

    setActiveGroup(null);
    setRestoring(false);
  };


  if (!visible) return null;

  return (
    <>
      {/* BACKDROP */}
      <div style={backdrop} onClick={onClose} />

      {/* MAIN PANEL */}
      <div style={panel}>
        {/* HEADER */}
        <div style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <RiDeleteBinLine size={22} color="#374151" />

        <div>
            <div style={title}>Papperskorg</div>
            <div style={subtitle}>Pass sparas i 90 dagar och raderas sen automatiskt</div>
        </div>
        </div>

          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* LIST */}
        <div style={list}>
          {groups.map((g) => {
            const first = g[0];

            const user =
              employees.find((e) => e._id === first.deletedBy)?.name ||
              "Okänd";

            const date = new Date(first.deletedAt).toLocaleString("sv-SE");

            return (
              <div
                key={first._id}
                style={groupRow}
                onClick={() => setActiveGroup(g)}
              >
                {/* LEFT */}
                <div>
                  <div style={groupTitle}>
                    {date}
                  </div>

                  <div style={groupSub}>
                    Raderades av {user}
                  </div>
                </div>

                {/* RIGHT */}
                <div style={groupRight}>
                  <div style={countBadge}>
                    {g.length} pass
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* DETAIL PANEL */}
      {activeGroup && (
        <div style={detailPanel}>
          {/* HEADER */}
          <div style={header}>
            <div style={title}>
              {activeGroup.length} pass
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                style={primaryBtn}
                onClick={() => restore(activeGroup)}
              >
                {restoring ? "Återställer..." : "Återställ alla"}
              </button>

              <button
                style={secondaryBtn}
                onClick={() => setActiveGroup(null)}
              >
                Stäng
              </button>
            </div>
          </div>

          {/* LIST */}
          <div style={detailList}>
            {activeGroup.map((shift) => {
              const project =
                projects.find((p) => p._id === shift.project)?.name || "-";

              const names =
                shift.employees
                  ?.map((e) =>
                    employees.find((emp) => emp._id === e.employeeId)?.name
                  )
                  .filter(Boolean)
                  .join(", ") || "-";

              return (
                <div key={shift._id} style={shiftRow}>
                  <div style={shiftTop}>
                    <span>{shift.date}</span>
                    <span style={time}>
                      {shift.startTime}–{shift.endTime}
                    </span>
                  </div>

                  <div style={shiftBottom}>
                    {project} • {names}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default TrashPopup;

/* ───────── STYLES ───────── */

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  zIndex: 999998,
};

const panel = {
  position: "fixed",
  right: 0,
  top: 0,
  width: 460,
  height: "100vh",
  background: "#f8fafc",
  zIndex: 999999,
  display: "flex",
  flexDirection: "column",
  borderLeft: "1px solid #e5e7eb",
};

const detailPanel = {
  position: "fixed",
  right: 460,
  top: 0,
  width: 540,
  height: "100vh",
  background: "#ffffff",
  zIndex: 1000000,
  display: "flex",
  flexDirection: "column",
  borderLeft: "1px solid #e5e7eb",
};

const header = {
  padding: "16px 18px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#fff",
};

const list = {
  padding: 12,
  overflowY: "auto",
};

const groupRow = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "12px 14px",
  marginBottom: 10,
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const groupTitle = {
  fontWeight: 600,
  fontSize: 14,
};

const groupSub = {
  fontSize: 12,
  color: "#6b7280",
};

const groupRight = {
  display: "flex",
  alignItems: "center",
};

const countBadge = {
  background: "#2563eb",
  color: "white",
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
};

const detailList = {
  padding: 16,
  overflowY: "auto",
};

const shiftRow = {
  padding: "12px 0",
  borderBottom: "1px solid #f1f5f9",
};

const shiftTop = {
  display: "flex",
  justifyContent: "space-between",
  fontWeight: 600,
};

const shiftBottom = {
  fontSize: 13,
  color: "#6b7280",
  marginTop: 2,
};

const time = {
  color: "#2563eb",
};

const primaryBtn = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "8px 14px",
  borderRadius: 6,
  cursor: "pointer",
  fontWeight: 600,
};

const secondaryBtn = {
  background: "#f3f4f6",
  border: "none",
  padding: "8px 14px",
  borderRadius: 6,
  cursor: "pointer",
};

const closeBtn = {
  background: "transparent",
  border: "none",
  fontSize: 20,
  cursor: "pointer",
};

const title = {
  fontSize: 18,
  fontWeight: 700,
};

const subtitle = {
  fontSize: 12,
  color: "#6b7280",
};

const trashIcon = {
  width: 28,
  height: 28,
  borderRadius: 6,
  background: "#fee2e2",     // 🔴 light red
  color: "#dc2626",          // 🔴 red icon
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};